/**
 * AcceptInvitePage — /accept-invite?token=X
 *
 * Flow:
 * 1. Reads token from URL query params
 * 2. Fetches invite info (public endpoint, no auth needed)
 * 3. If user is logged in → calls accept-invite API → redirects
 * 4. If user NOT logged in → shows invite info + Login/Register buttons
 *
 * After login/register, user is redirected back to /accept-invite?token=X
 * where the acceptance flow runs automatically.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AZURE } from '../services/api';
import { VUKZ } from '../theme/vukzTheme';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import {
  CheckCircle2, AlertTriangle, Loader2, Users, Shield,
  LogIn, UserPlus, Building2, Target,
} from 'lucide-react';

const ROLE_LABELS = {
  GESTOR: 'Gestor',
  TREINADOR: 'Treinador',
  PREPARADOR_FISICO: 'Preparador Físico',
  ATLETA: 'Atleta',
  RECRUITER: 'Recrutador',
};

const ROLE_ICONS = {
  GESTOR: Shield,
  TREINADOR: Target,
  PREPARADOR_FISICO: Target,
  ATLETA: Users,
  RECRUITER: Users,
};

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { refreshRBAC } = useRBAC();
  const token = searchParams.get('token') || '';

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [acceptResult, setAcceptResult] = useState(null);
  // FIX loop: auto-accept no máximo UMA vez POR TOKEN. Guardar o próprio
  // token (não um boolean) garante que, se a query mudar para outro convite,
  // o novo token não herda a tentativa do anterior. Sem isto, um 403
  // (ex.: e-mail divergente) re-disparava o POST infinitamente.
  const [attemptedToken, setAttemptedToken] = useState(null);

  // 1. Fetch invite info (public, no auth)
  useEffect(() => {
    if (!token) {
      setError('Link de convite inválido — token ausente.');
      setLoadingInfo(false);
      return;
    }

    const fetchInfo = async () => {
      try {
        const { data } = await AZURE.get('/onboarding/invite-info', { params: { token } });
        if (data.success) {
          setInviteInfo(data.data);
          if (data.data.status !== 'pending') {
            setError(`Este convite já foi ${data.data.status === 'accepted' ? 'aceito' : data.data.status}.`);
          }
        } else {
          setError(data.error || 'Convite não encontrado.');
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          setError('Convite não encontrado ou link inválido.');
        } else if (status === 410) {
          setError('Este convite expirou.');
        } else {
          setError('Erro ao carregar informações do convite.');
        }
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchInfo();
  }, [token]);

  // 2. Auto-accept if logged in + invite is valid
  const acceptInvite = useCallback(async () => {
    if (!user?.token || !token || accepting || accepted) return;
    setAttemptedToken(token);
    setAccepting(true);
    setError(null);
    try {
      const { data } = await AZURE.post('/onboarding/accept-invite', { token });
      if (data.success) {
        setAccepted(true);
        setAcceptResult(data.data);
        // INV-2c: reidrata roles/permissões SÓ depois do aceite — antes do
        // accept a conta é pending e /me retornaria 403 (roles=[] fail-closed).
        // Após pending→active, o /me devolve as roles reais do convíte.
        try {
          await refreshRBAC();
        } catch (rbacErr) {
          // Não bloqueia o redirect — AccessGuard/reload recuperam depois.
          console.error('[AcceptInvite] refreshRBAC falhou:', rbacErr);
        }
        // Auto-redirect after 2s — destino vem SEMPRE do backend.
        setTimeout(() => {
          navigate(data.data.redirect || '/central', { replace: true });
        }, 2500);
      } else {
        setError(data.error || 'Erro ao aceitar convite.');
      }
    } catch (err) {
      const msg = err?.response?.data?.error;
      setError(msg || 'Erro ao aceitar convite. Tente novamente.');
    } finally {
      setAccepting(false);
    }
  }, [user, token, accepting, accepted, navigate, refreshRBAC]);

  // Auto-trigger accept when user is logged in and invite info is loaded.
  // Invariante: auto-accept no máximo uma vez por token; retry é sempre
  // ação explícita do usuário.
  useEffect(() => {
    if (user?.token && inviteInfo && inviteInfo.status === 'pending'
        && !accepted && !accepting && attemptedToken !== token) {
      acceptInvite();
    }
  }, [user, inviteInfo, accepted, accepting, attemptedToken, token, acceptInvite]);

  const RoleIcon = inviteInfo ? (ROLE_ICONS[inviteInfo.role] || Users) : Users;
  const roleLabel = inviteInfo ? (ROLE_LABELS[inviteInfo.role] || inviteInfo.role) : '';

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#fafafa', padding: 24,
    }}>
      <div style={{
        width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16,
        border: `1px solid ${VUKZ.border}`, overflow: 'hidden',
      }}>
        {/* Header banner */}
        <div style={{
          background: VUKZ.text, padding: '24px 24px 20px', color: '#fff', textAlign: 'center',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: 'rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            {inviteInfo?.logo_url ? (
              <img
                src={inviteInfo.logo_url}
                alt=""
                style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <Building2 size={28} />
            )}
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>
            Convite para VUKZ AI
          </h1>
          {inviteInfo?.institution_name && (
            <p style={{ fontSize: 14, opacity: 0.85, margin: 0 }}>
              {inviteInfo.institution_name}
            </p>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: 24 }}>

          {/* Loading */}
          {loadingInfo && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Loader2 size={32} color={VUKZ.textLight} className="animate-spin" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: VUKZ.textLight, fontSize: 14 }}>Carregando convite...</p>
            </div>
          )}

          {/* Error state */}
          {!loadingInfo && error && !accepted && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <AlertTriangle size={32} color={VUKZ.error} style={{ margin: '0 auto 12px' }} />
              <p style={{ color: VUKZ.error, fontSize: 14, fontWeight: 500 }}>{error}</p>
              {/* E-mail divergente: o usuário está logado com OUTRA conta —
                  oferecer troca (logout concluído ANTES de navegar,
                  preservando o convite via redirect + invite_token). */}
              {user?.token && attemptedToken === token && (
                <button
                  onClick={async () => {
                    await logout();
                    navigate(
                      `/login?redirect=${encodeURIComponent(`/accept-invite?token=${token}`)}&invite_token=${encodeURIComponent(token)}`,
                      { replace: true },
                    );
                  }}
                  style={{
                    display: 'inline-block', marginTop: 16, marginRight: 8,
                    padding: '10px 24px', borderRadius: 8, background: VUKZ.text,
                    color: '#fff', fontSize: 14, fontWeight: 600, border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Entrar com outra conta
                </button>
              )}
              <Link
                to="/"
                style={{
                  display: 'inline-block', marginTop: 16, padding: '10px 24px',
                  borderRadius: 8, background: user?.token && attemptedToken === token ? '#fff' : VUKZ.text,
                  color: user?.token && attemptedToken === token ? VUKZ.text : '#fff',
                  border: user?.token && attemptedToken === token ? `1px solid ${VUKZ.border}` : 'none',
                  fontSize: 14, fontWeight: 600, textDecoration: 'none',
                }}
              >
                Ir para o início
              </Link>
            </div>
          )}

          {/* Accepted! */}
          {accepted && acceptResult && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <CheckCircle2 size={40} color="#16a34a" style={{ margin: '0 auto 12px' }} />
              <h2 style={{ fontSize: 18, fontWeight: 700, color: VUKZ.text, margin: '0 0 8px' }}>
                Convite aceito!
              </h2>
              <p style={{ fontSize: 14, color: VUKZ.textLight, margin: '0 0 4px' }}>
                Você agora é <strong>{ROLE_LABELS[acceptResult.role] || acceptResult.role}</strong>
              </p>
              <p style={{ fontSize: 13, color: VUKZ.textLight }}>
                Redirecionando...
              </p>
            </div>
          )}

          {/* Invite info loaded, user NOT logged in */}
          {!loadingInfo && !error && inviteInfo && inviteInfo.status === 'pending' && !user?.token && !accepted && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
                padding: '14px 16px', background: '#f3f4f6', borderRadius: 10,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: '#e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <RoleIcon size={20} color={VUKZ.text} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: VUKZ.text }}>
                    {inviteInfo.invite_name ? `${inviteInfo.invite_name} · ${roleLabel}` : roleLabel}
                  </div>
                  <div style={{ fontSize: 12, color: VUKZ.textLight }}>
                    Papel atribuído pelo convite
                    {inviteInfo.expires_at && (
                      <> · válido até {new Date(inviteInfo.expires_at).toLocaleDateString('pt-BR')}</>
                    )}
                  </div>
                </div>
              </div>

              <p style={{ fontSize: 14, color: VUKZ.textLight, marginBottom: 20, lineHeight: 1.5 }}>
                Você foi convidado para se juntar a <strong style={{ color: VUKZ.text }}>{inviteInfo.institution_name || 'uma organização'}</strong> como <strong style={{ color: VUKZ.text }}>{roleLabel}</strong>.
              </p>

              <p style={{ fontSize: 13, color: VUKZ.textLight, marginBottom: 20 }}>
                Para aceitar, faça login ou crie sua conta:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Link
                  to={`/login?redirect=${encodeURIComponent(`/accept-invite?token=${token}`)}&invite_token=${encodeURIComponent(token)}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '12px 20px', borderRadius: 10, background: VUKZ.text,
                    color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none',
                  }}
                >
                  <LogIn size={18} /> Entrar na minha conta
                </Link>
                <Link
                  to={`/invite-register?token=${encodeURIComponent(token)}`}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '12px 20px', borderRadius: 10, border: `1px solid ${VUKZ.border}`,
                    background: '#fff', color: VUKZ.text, fontSize: 14, fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  <UserPlus size={18} /> Criar minha conta
                </Link>
              </div>
            </div>
          )}

          {/* User logged in, accepting in progress */}
          {!loadingInfo && !error && inviteInfo && inviteInfo.status === 'pending' && user?.token && !accepted && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Loader2 size={32} color={VUKZ.text} className="animate-spin" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: VUKZ.text, fontSize: 14, fontWeight: 500 }}>
                Aceitando convite...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
